import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Flag } from "lucide-react";
import { LiveSignal, LiveNowFixture } from "@/types/api";
import { MarketBadge, CompetitionLabel, ResultBadge, TeamMono, RiskChip } from "@/components/badges/Badges";
import { TeamLogo, logoFrom, resolveTeamVisual } from "@/components/common/TeamLogo";
import { FollowButton, AddToSlipButton } from "@/components/cards/Actions";
import { MarketHubSections } from "@/components/cards/MarketHub";
import { parseDecisionReason, lifecycleOf, resultSurface } from "@/presentation/resultPresentation";
import { currentStateOf } from "@/presentation/liveState";
import { useLiveOverlay, useMarketHubMap } from "@/hooks/useAtlas";
import { presentOdd } from "@/presentation/oddsPresentation";
import { scoreLine } from "@/lib/format";
import { useI18n } from "@/i18n";

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center rounded-lg border border-white/5 bg-white/[0.02] px-1.5 py-1.5">
      <span className="num text-sm font-semibold text-foreground/90">{value}</span>
      <span className="mt-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
}

export function LiveMatchCard({ s, index = 0, currentLive }: { s: LiveSignal; index?: number; currentLive?: LiveNowFixture | null }) {
  const { d, lang } = useI18n();
  const key = lifecycleOf(s.status, s.won, { resolved_at: s.resolved_at, final_home_goals: s.final_home_goals, final_away_goals: s.final_away_goals });
  const resolved = key !== "PENDING";
  const dec = parseDecisionReason(s.decision_reason);
  // Authoritative current state. Prefer the live-now row merged in at page level
  // (keyed by fixture_id); otherwise fetch it via the deduped live-now query.
  // Both sources resolve to the SAME current minute/score for a given fixture.
  const auto = useLiveOverlay(s);
  const { lookup: hubLookup } = useMarketHubMap();
  const hub = hubLookup(s);
  // EVENT INTEGRITY: a fixture may carry multiple distinct signals. The fixture-
  // level Market Hub row is keyed by fixture_id, so only surface its selections
  // when its PRIMARY is the SAME event as this card's signal — otherwise this
  // card would borrow another event's market/odd/risk (e.g. a ONE_MORE_GOAL card
  // showing OVER_1_5 as "ATLAS ANA SEÇİMİ").
  const hubMatchesSignal = !!(hub?.primary && hub.primary.market_code === s.market_code);
  const live = currentLive ?? auto;
  const cur = currentStateOf(s, live, resolved);
  const homeScore = cur.home;
  const awayScore = cur.away;
  const dMinute = cur.minute;
  // Original signal-created minute, preserved separately from the CURRENT minute.
  const signalMinute = cur.signalMinute;
  const homeLogo = resolveTeamVisual(s.home_team, "home", [live, s]);
  const awayLogo = resolveTeamVisual(s.away_team, "away", [live, s]);
  const leagueLogo = logoFrom(live, "league") ?? logoFrom(s, "league");
  const momentum = s.direction || (dec.dominant === s.home_team ? "HOME" : dec.dominant === s.away_team ? "AWAY" : null);
  const odd = presentOdd({ value: s.odd, source: null }); // no source field -> hidden per trust rule
  const label = `${s.home_team} — ${s.away_team}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.3) }}
      className={`panel panel-hover group relative overflow-hidden p-4 ${resolved ? resultSurface(key).card : ""}`}
      data-testid="live-match-card"
    >
      {resolved && <span className={`absolute inset-y-0 left-0 w-1 ${resultSurface(key).accent}`} aria-hidden />}
      {!resolved && <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-live/10 blur-3xl" />}

      <div className="flex items-start justify-between gap-2">
        <CompetitionLabel country={s.country} league={s.league} leagueLogo={leagueLogo} />
        <div className="flex flex-col items-end gap-1.5">
          <ResultBadge status={s.status} won={s.won} isLive={!resolved} size="sm" />
          {!resolved && dMinute != null && (
            <span className="num rounded-md bg-live/10 px-2 py-0.5 text-[11px] font-bold text-live-400" data-testid="live-current-minute">{dMinute}'</span>
          )}
          {!resolved && signalMinute != null && signalMinute !== dMinute && (
            <span className="num text-[10px] font-medium text-muted-foreground" data-testid="live-signal-minute">{lang === "tr" ? "Sinyal" : "Signal"} {signalMinute}'</span>
          )}
        </div>
      </div>

      {/* PRIMARY: ATLAS recommendation dominates */}
      <div className="mt-4 rounded-xl border border-atlas/20 bg-atlas/[0.05] p-3">
        <p className="eyebrow mb-1.5 text-atlas-300">{lang === "tr" ? "ATLAS seçimi" : "ATLAS pick"}</p>
        <div className="flex items-center justify-between gap-2">
          <MarketBadge code={s.market_code} backendLabel={s.market_name} />
          <RiskChip code={s.market_code} />
        </div>
        {dec.headline && <p className="mt-2 text-[11px] text-muted-foreground">{dec.headline}</p>}
        {odd.show && (
          <p className="mt-1 text-[11px] text-muted-foreground">{d.common.refOdds}: <span className="num text-atlas-300">{odd.value}</span>{odd.source ? ` · ${odd.source}` : ""}</p>
        )}
      </div>

      {/* SECONDARY: teams + score */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <TeamLogo src={homeLogo} name={s.home_team} size={24} />
            <p className={`truncate text-sm font-semibold ${momentum === "HOME" ? "text-atlas-300" : "text-foreground"}`}>{s.home_team}</p>
          </div>
          <div className="flex items-center gap-2">
            <TeamLogo src={awayLogo} name={s.away_team} size={24} />
            <p className={`truncate text-sm font-semibold ${momentum === "AWAY" ? "text-atlas-300" : "text-foreground"}`}>{s.away_team}</p>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="num text-2xl font-bold tracking-tight text-foreground">{scoreLine(homeScore, awayScore)}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{resolved ? d.common.finalScore : d.common.score}</div>
        </div>
      </div>

      {/* TERTIARY: match stats */}
      {(dec.shots != null || dec.shotsOn != null || dec.corners != null || momentum) && (
        <div className="mt-3 grid grid-cols-4 gap-1.5">
          <Stat label={d.common.shots} value={dec.shots ?? "—"} />
          <Stat label={d.common.shotsOn} value={dec.shotsOn ?? "—"} />
          <Stat label={d.common.corners} value={dec.corners ?? "—"} />
          <div className="flex flex-col items-center rounded-lg border border-white/5 bg-white/[0.02] px-1 py-1.5">
            <span className="flex items-center gap-1 text-[11px] font-semibold text-foreground/80"><Flag className="h-3 w-3" />{momentum === "HOME" ? "Ev sh." : momentum === "AWAY" ? "Dep." : "—"}</span>
            <span className="mt-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">{d.common.momentum}</span>
          </div>
        </div>
      )}

      {hubMatchesSignal && hub && (
        <div className="mt-3 border-t border-white/5 pt-3">
          <MarketHubSections fixture={hub} variant="card" />
        </div>
      )}

      {/* actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <FollowButton fixtureId={s.fixture_id} label={label} league={s.league} size="sm" />
        <AddToSlipButton size="sm" selection={{ fixtureId: s.fixture_id, home: s.home_team, away: s.away_team, country: s.country, league: s.league, marketCode: s.market_code, marketName: s.market_name, confidence: s.model_probability, edge: s.edge, referenceOdd: s.odd, kickoff: s.created_at }} />
        <Link
          to={`/app/match/${s.fixture_id}`}
          data-testid="live-card-detail-link"
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-white/5 bg-white/[0.02] px-3 py-1.5 text-[11px] font-semibold text-muted-foreground transition hover:border-atlas/30 hover:text-atlas-300"
        >
          {lang === "tr" ? "Maç Merkezi" : "Match Center"} <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </motion.div>
  );
}
